/**
 * Minimal, data-oriented ECS.
 *
 * Deliberately tiny (no external lib) for Phase 0/1. Components are plain data
 * keyed by entity id, so the whole World serializes straight into a network
 * snapshot. If this grows, swap the internals for a lib like `bitecs` without
 * changing call sites much.
 */
import type { Vec2 } from "../math/vec2.js";
import { createRng, type Rng } from "../math/rng.js";
import {
  ARENAS,
  DEFAULT_ARENA,
  type ArenaId,
  type CharacterId,
} from "../constants/index.js";

export type EntityId = number;

export type EntityKind = "player" | "bot" | "projectile";

/** Position + velocity on the ground plane. */
export interface Transform {
  pos: Vec2;
  vel: Vec2;
}

export interface Health {
  current: number;
  max: number;
}

/** The latest movement/aim intent applied to an entity this tick. */
export interface Intent {
  move: Vec2;
  aim: Vec2;
  fire: boolean;
}

/** A live projectile's metadata (transform holds its pos/vel). */
export interface Projectile {
  owner: EntityId;
  damage: number;
  /** Remaining lifetime in ticks. */
  ttl: number;
  /** Collision/visual radius (carried per projectile, weapon-dependent). */
  radius: number;
}

/** Per-entity weapon state. */
export interface Weapon {
  /** Ticks remaining until the entity can fire again. */
  cooldown: number;
}

/** A fixed-slot health pickup. `cooldown` > 0 means consumed (respawning). */
export interface PickupSlot {
  pos: Vec2;
  cooldown: number;
}

/**
 * The full match state. Records (not Maps) so it is plain JSON for snapshots.
 * Component presence is sparse: an id may exist in `transform` but not `health`.
 */
export interface World {
  tick: number;
  nextEntityId: EntityId;
  /** Which arena this match runs in (drives obstacle collision). */
  arenaId: ArenaId;
  kind: Record<EntityId, EntityKind>;
  transform: Record<EntityId, Transform>;
  health: Record<EntityId, Health>;
  intent: Record<EntityId, Intent>;
  projectile: Record<EntityId, Projectile>;
  weapon: Record<EntityId, Weapon>;
  /** Which character each entity is (drives stats + weapon). */
  character: Record<EntityId, CharacterId>;
  /** Index-stable health pickup slots for the active arena. */
  pickups: PickupSlot[];
  /** Seeded RNG so all randomness (bot aim, spread) stays deterministic. */
  rng: Rng;
}

export const createWorld = (seed = 1, arenaId: ArenaId = DEFAULT_ARENA): World => ({
  tick: 0,
  nextEntityId: 1,
  arenaId,
  kind: {},
  transform: {},
  health: {},
  intent: {},
  projectile: {},
  weapon: {},
  character: {},
  pickups: ARENAS[arenaId].pickups.map((pos) => ({ pos: { x: pos.x, y: pos.y }, cooldown: 0 })),
  rng: createRng(seed),
});

export const spawnEntity = (world: World, kind: EntityKind): EntityId => {
  const id = world.nextEntityId++;
  world.kind[id] = kind;
  return id;
};

export const destroyEntity = (world: World, id: EntityId): void => {
  delete world.kind[id];
  delete world.transform[id];
  delete world.health[id];
  delete world.intent[id];
  delete world.projectile[id];
  delete world.weapon[id];
  delete world.character[id];
};

/** Iterates entity ids that have a transform — the common gameplay query. */
export const withTransform = (world: World): EntityId[] =>
  Object.keys(world.transform).map(Number);

/** Iterates entity ids of all live projectiles. */
export const withProjectile = (world: World): EntityId[] =>
  Object.keys(world.projectile).map(Number);

/** Iterates entity ids of a given kind. */
export const withKind = (world: World, kind: EntityKind): EntityId[] =>
  Object.keys(world.kind)
    .map(Number)
    .filter((id) => world.kind[id] === kind);
